import { InjectionToken, inject } from '@angular/core';

import { environment } from '../../../environments/environment';
import type {
  BrowserEnvironment,
  DeploymentEnvironment,
} from '../../../environments/environment.model';

export type { DeploymentEnvironment } from '../../../environments/environment.model';

/**
 * Public browser configuration only.
 *
 * Credentials, Reverb app secrets, database details, and other private values
 * belong on the backend/hosting environment and must never be added here.
 */
export type RuntimeConfig = BrowserEnvironment;

function baseUrl(value: string, protocols: readonly string[], label: string): string {
  let parsed: URL;

  try {
    parsed = new URL(value);
  } catch {
    throw new Error(`${label} must be an absolute URL.`);
  }

  if (!protocols.includes(parsed.protocol)) {
    throw new Error(`${label} must use ${protocols.join(' or ')}.`);
  }

  if (parsed.username || parsed.password || parsed.search || parsed.hash) {
    throw new Error(`${label} must not contain credentials, query parameters, or fragments.`);
  }

  return value.replace(/\/+$/, '');
}

function publicAppKey(value: string): string {
  const key = value.trim();

  if (key && !/^[A-Za-z0-9_-]{1,128}$/.test(key)) {
    throw new Error('Reverb public app key contains unsupported characters.');
  }

  return key;
}

function hospitalTimeZone(value: string): string {
  const zone = value.trim();

  try {
    new Intl.DateTimeFormat('en', { timeZone: zone }).format();
  } catch {
    throw new Error('Hospital time zone must be a valid IANA time zone.');
  }

  return zone;
}

function createRuntimeConfig(): Readonly<RuntimeConfig> {
  // The default source file is intentionally the live configuration, while
  // Angular file replacements provide the other union members at build time.
  const deployment = environment.environment as DeploymentEnvironment;
  const secureDeployment = deployment !== 'dev';
  const config: RuntimeConfig = {
    ...environment,
    apiBaseUrl: baseUrl(
      environment.apiBaseUrl,
      secureDeployment ? ['https:'] : ['http:', 'https:'],
      'API base URL',
    ),
    reverbBaseUrl: baseUrl(
      environment.reverbBaseUrl,
      secureDeployment ? ['wss:'] : ['ws:', 'wss:'],
      'Reverb base URL',
    ),
    hospitalTimeZone: hospitalTimeZone(environment.hospitalTimeZone),
    reverbAppKey: publicAppKey(environment.reverbAppKey),
  };

  return Object.freeze(config);
}

export const RUNTIME_CONFIG = new InjectionToken<Readonly<RuntimeConfig>>('RUNTIME_CONFIG', {
  providedIn: 'root',
  factory: createRuntimeConfig,
});

export const API_BASE_URL = new InjectionToken<string>('API_BASE_URL', {
  providedIn: 'root',
  factory: () => inject(RUNTIME_CONFIG).apiBaseUrl,
});

export const REVERB_BASE_URL = new InjectionToken<string>('REVERB_BASE_URL', {
  providedIn: 'root',
  factory: () => inject(RUNTIME_CONFIG).reverbBaseUrl,
});

/** Public Reverb application identifier; never the Reverb app secret. */
export const REVERB_APP_KEY = new InjectionToken<string>('REVERB_APP_KEY', {
  providedIn: 'root',
  factory: () => inject(RUNTIME_CONFIG).reverbAppKey,
});
